<template>
  <div
    ref="root"
    class="mt-8 border-t border-black/12 pt-6"
    :data-private-access-social="variant"
    aria-live="polite"
  >
    <p class="text-[0.66rem] uppercase tracking-[0.28em] text-black/60">
      {{ copy.eyebrow }}
    </p>
    <p class="mt-3 max-w-[28rem] text-[0.92rem] leading-[1.7] text-black/72">
      {{ copy.body }}
    </p>
    <a
      :href="instagramLink"
      target="_blank"
      rel="noopener noreferrer"
      class="mt-4 flex min-h-12 w-full max-w-full items-center justify-center gap-2 whitespace-nowrap border border-black bg-transparent px-4 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-black sm:inline-flex sm:w-auto sm:px-6 sm:tracking-[0.2em] no-underline transition hover:bg-black hover:text-[#fbf5eb] focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-black"
      :aria-label="copy.ariaLabel"
      @click="trackClick"
    >
      <Instagram class="h-4 w-4 shrink-0" :stroke-width="1.65" aria-hidden="true" />
      <span>{{ copy.cta }}</span>
      <ArrowUpRight class="h-3.5 w-3.5 shrink-0" :stroke-width="1.65" aria-hidden="true" />
    </a>
  </div>
</template>

<script setup lang="ts">
import { ArrowUpRight, Instagram } from 'lucide-vue-next'
import { getElementViewportMetrics } from '~/utils/signalskitViewport'

/**
 * Fallback micro-conversion for private access: when the visitor does not
 * leave an email, or right after they did, invite them to follow on Instagram.
 * Inline only (no popin). Reuses the existing instagram_follow_cta_* events
 * so Instagram clicks stay comparable with the magazine CTAs.
 */
const props = withDefaults(defineProps<{
  variant?: 'fallback' | 'success'
  formLocation?: string
}>(), {
  variant: 'fallback',
  formLocation: 'private_access'
})

const instagramLink = 'https://kendrick.paris/s/sig'
const route = useRoute()
const locale = useSiteLocale()
const signalsKit = import.meta.client ? useSignalsKit() : null
const root = ref<HTMLElement | null>(null)
let hasTrackedView = false

const COPY = {
  fr: {
    fallback: {
      eyebrow: 'Pas encore prête ?',
      body: 'Suivez Kendrick sur Instagram : les nouvelles paires et les ouvertures y sont dévoilées en avant-première.'
    },
    success: {
      eyebrow: 'Et en attendant',
      body: 'Suivez Kendrick sur Instagram pour voir la collection avant tout le monde.'
    },
    cta: 'Suivre @kendrick.paris',
    ariaLabel: 'Suivre Kendrick Paris sur Instagram (nouvel onglet)'
  },
  en: {
    fallback: {
      eyebrow: 'Not ready yet?',
      body: 'Follow Kendrick on Instagram: new pairs and openings are revealed there first.'
    },
    success: {
      eyebrow: 'In the meantime',
      body: 'Follow Kendrick on Instagram to see the collection before anyone else.'
    },
    cta: 'Follow @kendrick.paris',
    ariaLabel: 'Follow Kendrick Paris on Instagram (new tab)'
  }
}

const copy = computed(() => {
  const base = locale.value === 'fr' ? COPY.fr : COPY.en
  return { ...base[props.variant], cta: base.cta, ariaLabel: base.ariaLabel }
})

const track = (eventName: 'instagram_follow_cta_view' | 'instagram_follow_cta_click', entry?: IntersectionObserverEntry | null) => {
  signalsKit?.trackEditorialSignal(eventName, {
    page_path: route.path,
    locale: locale.value,
    interaction_type: eventName === 'instagram_follow_cta_view' ? 'view' : 'click',
    cta_location: props.variant === 'success' ? 'private_access_success' : 'private_access_fallback',
    cta_label: copy.value.cta,
    cta_component: 'private_access_social_fallback',
    form_location: props.formLocation,
    destination: instagramLink,
    outbound_url: instagramLink,
    social_platform: 'instagram',
    social_handle: 'kendrick.paris',
    ...getElementViewportMetrics(root.value, entry)
  })
}

const trackClick = () => track('instagram_follow_cta_click')

onMounted(() => {
  if (hasTrackedView) return
  hasTrackedView = true
  track('instagram_follow_cta_view')
})
</script>
